import { SpaceValues } from "@/lib/utils/SpaceValues";
import { StoreState } from "@/redux/RootReducer";
import {
  SearchFilterSelectorContext,
  SearchFilterSelectorContextType,
} from "@/features/search-filter/context/searchFilterSelectorContext";
import { createFppSearchFilterActions } from "@/features/search-filter/redux/reducer";
import { Space } from "@stenajs-webui/core";
import { Card, stenaBusinessAgreement } from "@stenajs-webui/elements";
import { SearchFilterContext } from "@stenajs-webui/filter";
import {
  StandardTable,
  StandardTableAction,
  TableContext,
} from "@stenajs-webui/grid";
import * as React from "react";
import { useCallback, useContext, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { PageHeader } from "@/components/page-header/PageHeader";
import { getGQLErrors } from "../../../../../lib/GraphQlErrorParser";
import {
  CustomerListTableColumnKeys,
  customerListTableConfig,
} from "../config/CustomerListTableConfig";
import { useCustomerListQuery } from "../hooks/UseCustomerListQuery";
import { customerListStandardTableActions } from "../redux";
import { customerListSelectors } from "../selectors";
import { transformCustomerToTableCustomer } from "../transformers";
import { CustomerListHeader } from "./CustomerListHeader";

interface Props {}

export const CustomerList: React.FC<Props> = () => {
  const dispatch = useDispatch();

  const { workspaceState, committedState } = useContext(
    SearchFilterSelectorContext
  ) as SearchFilterSelectorContextType;

  const {
    negotiationYear,
    sellers,
    negotiationStatus,
    salesRegions,
    routePairs,
    customerTypes,
    includeCustomersWith,
  } = workspaceState.formModel;

  const standardTableSelector = useCallback(
    (state: StoreState) => customerListSelectors.getTableState(state),
    []
  );
  const standardTableState = useSelector(standardTableSelector);

  const tableContext = useMemo<TableContext<CustomerListTableColumnKeys>>(
    () => ({
      state: standardTableState,
      actions: customerListStandardTableActions,
      dispatch: (action: StandardTableAction<CustomerListTableColumnKeys>) => {
        dispatch(action);
      },
    }),
    [dispatch, standardTableState]
  );

  const [sortOrder, setSortOrder] = useState(
    tableContext.state.sortOrder.sortBy
  );

  const selectedIds = tableContext.state.selectedIds.selectedIds;

  const config = useMemo(() => {
    return customerListTableConfig(committedState.negotiationYear);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isAllowedToSearch =
    negotiationYear &&
    (sellers.length > 0 || salesRegions.length > 0 || routePairs.length > 0);

  const { loading, error, customerList, fetchCustomerList, refetch } =
    useCustomerListQuery();

  const customers = useMemo(
    () => customerList?.map(transformCustomerToTableCustomer),
    [customerList]
  );

  const noItemsLabel = isAllowedToSearch
    ? "Found no matching customers. Please use filter to change the search."
    : "Please select negotiation year, and at least seller or region to search for customers.";

  const onSearch = () => {
    isAllowedToSearch &&
      fetchCustomerList({
        variables: {
          negotiationYear,
          lastNegotiationYear: negotiationYear - 1,
          filter: {
            negotiationYear,
            includeCustomersWith: includeCustomersWith.map(
              (includeCustomerWith) => includeCustomerWith.id
            ),
            negotiationStatusIds: negotiationStatus.map(
              (negotiationStatus) => negotiationStatus.id
            ),
            routePairIds: routePairs.map((routePair) => routePair.id),
            salesRegionIds: salesRegions.map((salesRegion) => salesRegion.id),
            sellerIds: sellers.map((seller) => seller.id),
            types: customerTypes.map((customerType) => customerType.id),
          },
        },
      });
  };

  React.useEffect(() => {
    onSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <SearchFilterContext
      state={workspaceState}
      actions={createFppSearchFilterActions("customerList")}
      dispatch={dispatch}
    >
      <PageHeader icon={stenaBusinessAgreement} title={"Negotiation plan"} />
      <Card>
        <CustomerListHeader
          loading={loading}
          selectedIds={selectedIds}
          tableContext={tableContext}
          negotiationYear={negotiationYear}
          refetch={refetch}
          numShowing={customers?.length ?? 0}
          onSearch={onSearch}
        />

        <StandardTable
          config={config}
          items={customers ? customers : []}
          noItemsLabel={noItemsLabel}
          tableContext={tableContext}
          loading={loading}
          error={error ? getGQLErrors(error) : undefined}
        />
      </Card>
      <Space num={SpaceValues.TWENTYFOUR} />
    </SearchFilterContext>
  );
};
